import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import Pagination from '../components/common/Pagination';
import { 
  MessageCircle, Search, Plus, Clock, CheckCircle2, 
  AlertCircle, MessageSquare, ChevronRight, Lock, Flame,
  HelpCircle, Copy, ChevronDown, Menu, Award, MapPin,
  User, X, FileText, Mic, Globe, Bookmark, BookmarkCheck
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import SubscriptionGate from '../components/common/SubscriptionGate';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  answered: { label: 'Answered', color: 'bg-amber-100 text-amber-700', icon: MessageSquare },
  verified: { label: 'Verified', color: 'bg-green-100 text-green-700', icon: CheckCircle2 }
};

// Generate consistent color from name for user initials
const getInitialColor = (name) => {
  const colors = ['#0A7EA4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#6366F1'];
  const hash = (name || 'U').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Get initials from name
const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
};

// User Avatar component with initials
const UserInitials = ({ name, size = 'sm', role }) => {
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base'
  };
  
  return (
    <div 
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center text-white font-medium flex-shrink-0`}
      style={{ backgroundColor: getInitialColor(name) }}
      title={name}
    >
      {role === 'admin' || role === 'sub_admin' ? (
        <Award className="w-3 h-3" />
      ) : (
        getInitials(name)
      )}
    </div>
  );
};

// Topic tree item component (iter 105: fully recursive, any depth).
// Takes expandedIds (Set) and selectedId (string|null) rather than
// per-node booleans, so recursion can re-derive per child.
const TopicTreeItem = ({ topic, childTopics, expandedIds, onToggle, selectedId, onSelect, level = 0 }) => {
  const hasChildren = childTopics && childTopics.length > 0;
  const hasQuestions = topic.total_questions > 0;
  const isGeneral = topic.is_general || topic.id === 'general';
  const isExpanded = expandedIds ? expandedIds.has(topic.id) : false;
  const isSelected = selectedId === topic.id;
  
  return (
    <div>
      <div 
        className={`flex items-start gap-2 py-2 px-2 rounded cursor-pointer hover:bg-gray-100 ${
          isSelected ? 'bg-[#0A7EA4]/10 text-[#0A7EA4]' : ''
        } ${isGeneral ? 'border-b border-gray-200 mb-1' : ''}`}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
        onClick={() => onSelect(isGeneral ? 'general' : topic.id)}
      >
        {hasChildren && (
          <button 
            onClick={(e) => { e.stopPropagation(); onToggle(topic.id); }}
            className="p-0.5 hover:bg-gray-200 rounded mt-0.5 flex-shrink-0"
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        )}
        {!hasChildren && <span className="w-4 flex-shrink-0" />}
        <span className={`flex-1 min-w-0 text-sm break-words line-clamp-2 leading-snug ${
          isGeneral ? 'text-gray-500 italic' : (!hasQuestions ? 'text-gray-400' : '')
        }`}>
          {topic.name}
        </span>
        {(topic.total_questions > 0 || isGeneral) && (
          <Badge variant="outline" className={`text-xs px-1.5 py-0 flex-shrink-0 ${isGeneral ? 'bg-gray-100 text-gray-500' : ''}`}>
            {topic.total_questions}
            {topic.unanswered > 0 && (
              <span className="ml-1 text-red-500">({topic.unanswered})</span>
            )}
          </Badge>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div>
          {childTopics.map(child => (
            <TopicTreeItem
              key={child.id}
              topic={child}
              childTopics={child.children || []}
              expandedIds={expandedIds}
              onToggle={onToggle}
              selectedId={selectedId}
              onSelect={onSelect}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Topic Sidebar Content component with Search
const TopicSidebarContent = ({ 
  topics, 
  hierarchicalTopics, 
  selectedTopic, 
  setSelectedTopic, 
  expandedTopics, 
  toggleTopicExpanded, 
  setSidebarOpen,
  contributors,
  totalQuestions
}) => {
  const [topicSearch, setTopicSearch] = useState('');
  
  // Filter topics based on search (iter 105: recursive so grandchild+
  // matches surface, and every ancestor of a match is kept in the tree).
  const filteredTopics = useMemo(() => {
    if (!topicSearch.trim()) return hierarchicalTopics;
    const searchLower = topicSearch.toLowerCase();

    const prune = (node) => {
      const selfMatches = (node.name || '').toLowerCase().includes(searchLower);
      const prunedChildren = (node.children || [])
        .map(prune)
        .filter(Boolean);
      if (selfMatches || prunedChildren.length > 0) {
        return { ...node, children: selfMatches ? node.children : prunedChildren };
      }
      return null;
    };

    return hierarchicalTopics.map(prune).filter(Boolean);
  }, [hierarchicalTopics, topicSearch]);
  
  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">Topics</h3>
          {selectedTopic && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSelectedTopic('')}
              className="text-xs h-6 px-2"
            >
              Clear
            </Button>
          )}
        </div>
        
        {/* Topic Search */}
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <Input
            placeholder="Search topics..."
            value={topicSearch}
            onChange={(e) => setTopicSearch(e.target.value)}
            className="pl-7 h-8 text-sm"
            data-testid="topic-sidebar-search"
          />
          {topicSearch && (
            <button 
              onClick={() => setTopicSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        
        <Button
          variant={!selectedTopic ? 'secondary' : 'ghost'}
          size="sm"
          className="w-full justify-start text-sm"
          onClick={() => setSelectedTopic('')}
        >
          All Topics
          <Badge variant="outline" className="ml-auto">
            {totalQuestions ?? topics.reduce((sum, t) => sum + (t.parent_topic_id ? 0 : t.total_questions), 0)}
          </Badge>
        </Button>
      </div>
      
      <ScrollArea className="flex-1 p-2">
        {filteredTopics.length === 0 && topicSearch && (
          <p className="text-sm text-gray-500 text-center py-4">No topics match &ldquo;{topicSearch}&rdquo;</p>
        )}
        {filteredTopics.map(topic => (
          <TopicTreeItem
            key={topic.id || 'general'}
            topic={topic}
            childTopics={topic.children}
            expandedIds={topicSearch ? new Set(topics.map(t => t.id)) : expandedTopics}
            onToggle={toggleTopicExpanded}
            selectedId={selectedTopic}
            onSelect={(id) => { setSelectedTopic(id); if (setSidebarOpen) setSidebarOpen(false); }}
          />
        ))}
      </ScrollArea>

      {/* Top Contributors */}
      {contributors.length > 0 && (
        <div className="border-t p-3">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Top Contributors</h4>
          <div className="space-y-2">
            {contributors.slice(0, 3).map((c, idx) => (
              <div key={c.user_id} className="flex items-center gap-2">
                <span className="text-sm">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                <UserInitials name={c.user_name} size="xs" role={c.user_role} />
                <span className="text-xs text-gray-700 truncate flex-1">{c.user_name}</span>
                <span className="text-xs text-green-600">{c.verified_answers}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ForumPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { preferences } = usePreferences();
  const { getForumStatus, loading: moduleLoading } = useModuleAccess();
  
  // Data state
  const [statsSummary, setStatsSummary] = useState(null);
  const [functions, setFunctions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topicsTotalQuestions, setTopicsTotalQuestions] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UI state - Function selection
  const [mmdOnly, setMmdOnly] = useState(searchParams.get('mmd') === 'true');
  const [selectedOralFunction, setSelectedOralFunction] = useState(searchParams.get('oral_func') || '');
  const [selectedSubFunction, setSelectedSubFunction] = useState(searchParams.get('sub_func') || ''); // ID of selected oral/written sub-function
  const [selectedTopic, setSelectedTopic] = useState(searchParams.get('topic') || '');
  const [activeFilter, setActiveFilter] = useState(searchParams.get('filter') || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [expandedTopics, setExpandedTopics] = useState(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Bookmarks
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [bookmarksPage, setBookmarksPage] = useState(1);
  const [bookmarksTotal, setBookmarksTotal] = useState(0);
  const [bookmarksTotalPages, setBookmarksTotalPages] = useState(0);

  // Module and access checks
  const forumStatus = getForumStatus();
  const { subscribedGrade } = useSubscriptionAccess();
  const hasPaidAccess = useMemo(() => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'sub_admin') return true;
    return user.subscription_status === 'paid';
  }, [user]);
  // Forum content is grade-scoped server-side by `preferences.grade`. Paid
  // Class-2 users switching preference to Class-4 must NOT be able to read
  // Chief-Mate forum posts they haven't paid for — enforce the same
  // subscribed_grade === currentGrade rule as Progress Tracker / Notebook.
  const isStaff = user?.role === 'admin' || user?.role === 'sub_admin';
  const hasGradeAccess =
    isStaff || !preferences?.grade || subscribedGrade === 'all' || subscribedGrade === preferences.grade;
  const hasFullAccess = hasPaidAccess && hasGradeAccess;

  // Build hierarchical topics (iter 105: recursive so grandchild+
  // topics render — was 2-level before).
  const hierarchicalTopics = useMemo(() => {
    const byParent = new Map();
    topics.forEach((t) => {
      const parent = t.parent_topic_id || null;
      if (!byParent.has(parent)) byParent.set(parent, []);
      byParent.get(parent).push(t);
    });
    const attach = (node) => ({
      ...node,
      children: (byParent.get(node.id) || []).map(attach),
    });
    return (byParent.get(null) || []).map(attach);
  }, [topics]);

  // Get current selected function details
  const selectedOralFunctionData = useMemo(() => {
    return functions.find(f => f.id === selectedOralFunction);
  }, [functions, selectedOralFunction]);

  // Build sub-function options (Oral + Written functions)
  const subFunctionOptions = useMemo(() => {
    if (!selectedOralFunctionData) return [];
    
    const options = [
      {
        id: selectedOralFunctionData.id,
        name: `${selectedOralFunctionData.name} (Oral)`,
        shortName: 'Oral',
        type: 'oral',
        total_questions: selectedOralFunctionData.total_questions,
        unanswered: selectedOralFunctionData.unanswered
      }
    ];
    
    // Add all written functions
    if (selectedOralFunctionData.written_functions) {
      for (const written of selectedOralFunctionData.written_functions) {
        options.push({
          id: written.id,
          name: written.name,
          shortName: written.name,
          type: 'written',
          total_questions: written.total_questions,
          unanswered: written.unanswered
        });
      }
    }
    
    return options;
  }, [selectedOralFunctionData]);

  // Grouped options for the Live-Classes-style filter card.
  // `orals` and `writtens` are flat lists across every function in the grade —
  // this replaces the older two-row "family → sub-function" layout that
  // confused users because it didn't label the two groups.
  const groupedFunctionOptions = useMemo(() => {
    const orals = [];
    const writtens = [];
    (functions || []).forEach(f => {
      orals.push({
        id: f.id,
        name: f.name,
        function_number: f.function_number,
        type: 'oral',
        parent_oral_id: f.id,
        total_questions: f.total_questions,
      });
      (f.written_functions || []).forEach(w => {
        writtens.push({
          id: w.id,
          name: w.name,
          function_number: f.function_number, // keep parent oral's F-number for label
          type: 'written',
          parent_oral_id: f.id,
          total_questions: w.total_questions,
        });
      });
    });
    return { orals, writtens };
  }, [functions]);

  // Get the actual function ID to query questions
  const currentFunctionId = useMemo(() => {
    return selectedSubFunction || selectedOralFunction || null;
  }, [selectedSubFunction, selectedOralFunction]);

  const fetchStatsSummary = useCallback(async () => {
    if (!hasFullAccess) return;
    try {
      const response = await axios.get(`${API}/forum/stats-summary`);
      setStatsSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch stats summary:', error);
    }
  }, [hasFullAccess]);

  const fetchFunctions = useCallback(async () => {
    if (!hasFullAccess) return;
    try {
      const response = await axios.get(`${API}/forum/functions-with-counts`, {
        params: { mmd_only: mmdOnly }
      });
      setFunctions(response.data.functions || []);
      
      // Auto-select first function with questions, or just first function
      if (!selectedOralFunction && response.data.functions?.length > 0) {
        const firstWithQuestions = response.data.functions.find(f => 
          f.total_questions > 0 || (f.written_functions?.some(w => w.total_questions > 0))
        );
        const toSelect = firstWithQuestions || response.data.functions[0];
        setSelectedOralFunction(toSelect.id);
        // Default to oral sub-function
        setSelectedSubFunction(toSelect.id);
      }
    } catch (error) {
      console.error('Failed to fetch functions:', error);
    }
  }, [hasFullAccess, mmdOnly, selectedOralFunction]);

  const fetchTopics = useCallback(async () => {
    if (!hasFullAccess || !currentFunctionId) {
      setTopics([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/forum/topics-with-counts`, {
        params: { function_id: currentFunctionId, mmd_only: mmdOnly }
      });
      setTopics(response.data.topics || []);
      setTopicsTotalQuestions(response.data.total_questions || 0);
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  }, [hasFullAccess, currentFunctionId, mmdOnly]);

  const fetchBookmarks = useCallback(async () => {
    if (!hasFullAccess) return;
    
    try {
      setBookmarksLoading(true);
      const response = await axios.get(`${API}/forum/bookmarks`, {
        params: { page: bookmarksPage, page_size: itemsPerPage }
      });
      setBookmarks(response.data.questions || []);
      setBookmarksTotal(response.data.total || 0);
      setBookmarksTotalPages(response.data.total_pages || 0);
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    } finally {
      setBookmarksLoading(false);
    }
  }, [hasFullAccess, bookmarksPage, itemsPerPage]);

  const fetchQuestions = useCallback(async () => {
    if (!hasFullAccess) return;
    
    try {
      setLoading(true);
      const params = {
        page,
        page_size: itemsPerPage,
        mmd_only: mmdOnly
      };
      
      if (currentFunctionId) params.function_id = currentFunctionId;
      if (selectedTopic) {
        // Handle "general" topic - filter for questions with no topic
        if (selectedTopic === 'general') {
          params.no_topic = true;
        } else {
          params.topic_id = selectedTopic;
        }
      }
      if (activeFilter) params.filter_type = activeFilter;
      
      const response = await axios.get(`${API}/forum/questions`, { params });
      setQuestions(response.data.questions);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      if (error.response?.status === 403) {
        toast.error('Forum access requires a paid subscription');
      }
    } finally {
      setLoading(false);
    }
  }, [hasFullAccess, page, itemsPerPage, currentFunctionId, selectedTopic, activeFilter, mmdOnly]);

  const fetchContributors = useCallback(async () => {
    if (!hasFullAccess) return;
    try {
      const response = await axios.get(`${API}/forum/top-contributors`, {
        params: { function_id: currentFunctionId || undefined }
      });
      setContributors(response.data.contributors || []);
    } catch (error) {
      console.error('Failed to fetch contributors:', error);
    }
  }, [hasFullAccess, currentFunctionId]);

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 3) {
      toast.error('Please enter at least 3 characters to search');
      return;
    }
    
    try {
      setIsSearching(true);
      setLoading(true);
      const response = await axios.get(`${API}/forum/search`, {
        params: {
          query: searchQuery,
          function_id: currentFunctionId || undefined,
          page: 1,
          page_size: itemsPerPage
        }
      });
      setQuestions(response.data.questions);
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
      setTotal(response.data.total);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    fetchQuestions();
  };

  const copyLink = (questionId) => {
    const url = `${window.location.origin}/forum/question/${questionId}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const toggleTopicExpanded = (topicId) => {
    setExpandedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  };

  // Single-select entry point used by the Live-Classes-style filter card.
  // Written pills need their parent oral function so the "family" state
  // (used for topic-sidebar loading and URL params) stays in sync.
  const handleFunctionPillSelect = (option) => {
    if (!option) return;
    if (option.type === 'oral') {
      setSelectedOralFunction(option.id);
      setSelectedSubFunction(option.id);
    } else {
      setSelectedOralFunction(option.parent_oral_id);
      setSelectedSubFunction(option.id);
    }
    setSelectedTopic('');
    setPage(1);
    setIsSearching(false);
  };

  // Effects
  useEffect(() => {
    if (isAuthenticated() && preferences.grade && hasFullAccess) {
      fetchStatsSummary();
      fetchFunctions();
    }
  }, [isAuthenticated, preferences.grade, hasFullAccess, fetchStatsSummary, fetchFunctions]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  useEffect(() => {
    if (!isSearching) {
      fetchQuestions();
    }
  }, [fetchQuestions, isSearching]);

  useEffect(() => {
    fetchContributors();
  }, [fetchContributors]);

  // Fetch bookmarks when bookmarks tab is active
  useEffect(() => {
    if (showBookmarks && hasFullAccess) {
      fetchBookmarks();
    }
  }, [showBookmarks, hasFullAccess, fetchBookmarks]);

  useEffect(() => {
    // Update URL params
    const params = new URLSearchParams();
    if (selectedOralFunction) params.set('oral_func', selectedOralFunction);
    if (selectedSubFunction && selectedSubFunction !== selectedOralFunction) {
      params.set('sub_func', selectedSubFunction);
    }
    if (selectedTopic) params.set('topic', selectedTopic);
    if (activeFilter) params.set('filter', activeFilter);
    if (mmdOnly) params.set('mmd', 'true');
    setSearchParams(params, { replace: true });
  }, [selectedOralFunction, selectedSubFunction, selectedTopic, activeFilter, mmdOnly, setSearchParams]);

  // Access checks
  if (moduleLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  if (forumStatus === 'hidden') {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto text-center py-16">
          <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Feature Not Available</h2>
          <p className="text-gray-600 mb-6">The community forum is currently not available.</p>
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </div>
      </Layout>
    );
  }

  if (forumStatus === 'coming_soon') {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto text-center py-16">
          <MessageCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <Badge className="bg-amber-100 text-amber-700 mb-4">Coming Soon</Badge>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Community Forum</h2>
          <p className="text-gray-600 mb-6">
            We&apos;re building a community space where you can ask questions, share knowledge, 
            and learn together with other maritime professionals. Stay tuned!
          </p>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            Go to Dashboard
          </Button>
        </div>
      </Layout>
    );
  }

  if (!isAuthenticated()) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto text-center py-16">
          <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Login Required</h2>
          <p className="text-gray-600 mb-6">Please login to access the community forum.</p>
          <Button onClick={() => navigate('/')}>Go to Home</Button>
        </div>
      </Layout>
    );
  }

  if (!hasFullAccess) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-12">
          <SubscriptionGate
            hasAccess={false}
            featureName="Community Forum"
            moduleKey="forum"
            subscribedGrade={subscribedGrade}
            targetGrade={preferences?.grade}
            description="Join grade-specific discussions with fellow maritime professionals. Ask questions, share experiences, and learn from those who've been through the exam you're preparing for."
          />
        </div>
      </Layout>
    );
  }

  // Get selected sub-function details for display
  const selectedSubFunctionData = subFunctionOptions.find(sf => sf.id === selectedSubFunction);

  return (
    <Layout>
      <div className="page-transition">
        {/* Header */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#0A7EA4]/10 rounded-lg">
                <MessageCircle className="w-5 h-5 text-[#0A7EA4]" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900">Community Forum</h1>
                <p className="text-sm text-gray-600 hidden sm:block">{preferences.grade}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={showBookmarks ? "default" : "outline"}
                onClick={() => setShowBookmarks(!showBookmarks)}
                className={showBookmarks ? "bg-amber-500 hover:bg-amber-600" : ""}
                data-testid="bookmarks-btn"
              >
                {showBookmarks ? (
                  <BookmarkCheck className="w-4 h-4 mr-1 md:mr-2" />
                ) : (
                  <Bookmark className="w-4 h-4 mr-1 md:mr-2" />
                )}
                <span className="hidden sm:inline">Bookmarks</span>
                {bookmarksTotal > 0 && !showBookmarks && (
                  <Badge variant="secondary" className="ml-1">{bookmarksTotal}</Badge>
                )}
              </Button>
              <Button 
                onClick={() => navigate('/forum/new')}
                className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
                data-testid="new-question-btn"
              >
                <Plus className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Ask Question</span>
                <span className="sm:hidden">Ask</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Bookmarks View */}
        {showBookmarks ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BookmarkCheck className="w-5 h-5 text-amber-500" />
                My Bookmarks ({bookmarksTotal})
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowBookmarks(false)}>
                <X className="w-4 h-4 mr-1" /> Close
              </Button>
            </div>
            
            {bookmarksLoading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : bookmarks.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Bookmark className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No Bookmarks Yet</h3>
                  <p className="text-gray-600 mb-4">
                    Save questions for quick access by clicking the bookmark icon on any question.
                  </p>
                  <Button variant="outline" onClick={() => setShowBookmarks(false)}>
                    Browse Questions
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {bookmarks.map(question => {
                  const statusConfig = STATUS_CONFIG[question.status];
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <Card 
                      key={question.id}
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/forum/question/${question.id}`)}
                      data-testid={`bookmark-${question.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge className={statusConfig.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                              {question.function_name && (
                                <Badge variant="outline" className="max-w-full whitespace-normal break-words text-left">{question.function_name}</Badge>
                              )}
                              <BookmarkCheck className="w-4 h-4 text-amber-500" />
                            </div>
                            <h3 className="font-medium text-gray-900 mb-1 line-clamp-2">
                              {question.title}
                            </h3>
                            <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                              {question.content}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {question.user_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                {question.answer_count} answers
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Saved {question.bookmarked_at ? formatDistanceToNow(new Date(question.bookmarked_at), { addSuffix: true }) : 'recently'}
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                
                {/* Bookmarks Pagination */}
                {bookmarksTotal > 0 && (
                  <div className="mt-6">
                    <Pagination
                      currentPage={bookmarksPage}
                      totalItems={bookmarksTotal}
                      itemsPerPage={itemsPerPage}
                      onPageChange={(newPage) => setBookmarksPage(newPage)}
                      onItemsPerPageChange={(newSize) => {
                        setItemsPerPage(newSize);
                        setBookmarksPage(1);
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <>
        {/* MMD Toggle - Prominent at top */}
        {statsSummary && (
          <div className="mb-4">
            <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
              <button
                onClick={() => { setMmdOnly(false); setPage(1); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  !mmdOnly 
                    ? 'bg-white shadow-sm text-[#0A7EA4]' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="all-grade-toggle"
              >
                <Globe className="w-4 h-4" />
                All {preferences.grade}
                <Badge variant="outline" className={!mmdOnly ? 'bg-[#0A7EA4]/10 text-[#0A7EA4]' : ''}>
                  {statsSummary.all_grade_total}
                </Badge>
              </button>
              <button
                onClick={() => { setMmdOnly(true); setPage(1); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  mmdOnly 
                    ? 'bg-white shadow-sm text-purple-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="mmd-only-toggle"
              >
                <MapPin className="w-4 h-4" />
                {statsSummary.mmd_location?.replace(' MMD', '')} Only
                <Badge variant="outline" className={mmdOnly ? 'bg-purple-100 text-purple-600' : ''}>
                  {statsSummary.mmd_only_total}
                </Badge>
              </button>
            </div>
          </div>
        )}

        {/* Function filter — Live-Classes-style labelled card so students
            can tell Oral from Written at a glance. Single-select: picking
            any pill sets it as the current view. */}
        <div className="mb-4 p-3 rounded-lg border bg-gray-50/60" data-testid="forum-function-filter">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Filter by function</p>
            <div className="flex items-center gap-2 text-[10px] text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-[#0A7EA4] border border-[#0A7EA4]" />
                Orals
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-purple-600 border border-purple-600" />
                Written
              </span>
            </div>
          </div>

          {[
            { key: 'orals', label: 'Orals', items: groupedFunctionOptions.orals, activeCls: 'bg-[#0A7EA4] text-white border-[#0A7EA4]', hoverCls: 'hover:border-[#0A7EA4] hover:text-[#0A7EA4]' },
            { key: 'written', label: 'Written', items: groupedFunctionOptions.writtens, activeCls: 'bg-purple-600 text-white border-purple-600', hoverCls: 'hover:border-purple-500 hover:text-purple-700' },
          ].filter(g => g.items.length > 0).map(group => (
            <div key={group.key} className="mb-2 last:mb-0" data-testid={`forum-function-group-${group.key}`}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                {group.label}
                <span className="ml-1 text-gray-400 font-normal">({group.items.length})</span>
              </p>
              <div className="flex flex-wrap gap-1.5">
                {group.items.map(opt => {
                  const active = selectedSubFunction === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => handleFunctionPillSelect(opt)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors max-w-full whitespace-normal break-words text-left leading-snug ${active
                        ? group.activeCls
                        : `bg-white text-gray-700 border-gray-300 ${group.hoverCls}`}`}
                      data-testid={`forum-function-chip-${opt.id}`}
                      title={opt.name}
                    >
                      <span className="opacity-70 mr-1">F{opt.function_number}</span>
                      {opt.name}
                      {opt.total_questions > 0 && (
                        <span className={`ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-4 rounded-full text-[10px] px-1 ${active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>
                          {opt.total_questions}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Current Selection Indicator */}
        {selectedSubFunctionData && (
          <div className="mb-4 flex items-start gap-2 text-sm flex-wrap">
            <span className="text-gray-500 flex-shrink-0">Currently viewing:</span>
            <Badge 
              className={`max-w-full whitespace-normal break-words text-left leading-snug ${
                selectedSubFunctionData.type === 'oral' 
                  ? 'bg-[#0A7EA4]/10 text-[#0A7EA4]' 
                  : 'bg-purple-100 text-purple-700'
              }`}
            >
              {selectedSubFunctionData.type === 'oral' ? (
                <Mic className="w-3 h-3 mr-1 flex-shrink-0" />
              ) : (
                <FileText className="w-3 h-3 mr-1 flex-shrink-0" />
              )}
              F{selectedOralFunctionData?.function_number} - {selectedSubFunctionData.name}
            </Badge>
          </div>
        )}

        {/* Main Content */}
        <div className="flex gap-4">
          {/* Topic Sidebar - Desktop */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <Card className="sticky top-4 h-[calc(100vh-320px)]">
              <TopicSidebarContent 
                topics={topics}
                hierarchicalTopics={hierarchicalTopics}
                selectedTopic={selectedTopic}
                setSelectedTopic={setSelectedTopic}
                expandedTopics={expandedTopics}
                toggleTopicExpanded={toggleTopicExpanded}
                setSidebarOpen={null}
                contributors={contributors}
                totalQuestions={topicsTotalQuestions}
              />
            </Card>
          </div>

          {/* Questions List */}
          <div className="flex-1 min-w-0">
            {/* Search and Filters */}
            <Card className="mb-4">
              <CardContent className="p-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Mobile Topic Button */}
                  <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="lg:hidden">
                        <Menu className="w-4 h-4 mr-2" />
                        Topics
                        {selectedTopic && <Badge className="ml-2">1</Badge>}
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-80 p-0">
                      <SheetHeader className="p-4 border-b">
                        <SheetTitle>Browse Topics</SheetTitle>
                      </SheetHeader>
                      <TopicSidebarContent 
                        topics={topics}
                        hierarchicalTopics={hierarchicalTopics}
                        selectedTopic={selectedTopic}
                        setSelectedTopic={setSelectedTopic}
                        expandedTopics={expandedTopics}
                        toggleTopicExpanded={toggleTopicExpanded}
                        setSidebarOpen={setSidebarOpen}
                        contributors={contributors}
                        totalQuestions={topicsTotalQuestions}
                      />
                    </SheetContent>
                  </Sheet>

                  {/* Search */}
                  <div className="flex-1 flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search questions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        className="pl-10"
                        data-testid="forum-search-input"
                      />
                    </div>
                    <Button onClick={handleSearch} variant="outline" size="icon">
                      <Search className="w-4 h-4" />
                    </Button>
                    {isSearching && (
                      <Button onClick={clearSearch} variant="ghost" size="icon">
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Filter Pills */}
                <div className="flex gap-2 mt-3 overflow-x-auto scrollbar-hide pb-1">
                  {[
                    { id: '', label: 'All', icon: MessageCircle },
                    { id: 'unanswered', label: 'Unanswered', icon: HelpCircle },
                    { id: 'recent', label: 'Recent', icon: Clock },
                    { id: 'my_questions', label: 'My Questions', icon: User },
                    { id: 'my_answers', label: 'My Answers', icon: MessageSquare }
                  ].map(filter => (
                    <button
                      key={filter.id}
                      onClick={() => { setActiveFilter(filter.id); setPage(1); setIsSearching(false); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                        activeFilter === filter.id
                          ? 'bg-[#0A7EA4] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`filter-${filter.id || 'all'}`}
                    >
                      <filter.icon className="w-3.5 h-3.5" />
                      {filter.label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Results Info */}
            {!loading && (
              <div className="mb-3 text-sm text-gray-600">
                {isSearching ? (
                  <span>Found {total} results for &ldquo;{searchQuery}&rdquo;</span>
                ) : (
                  <span>Showing {questions.length} of {total} questions</span>
                )}
              </div>
            )}

            {/* Questions */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : questions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No questions yet</h3>
                  <p className="text-gray-500 mb-4">
                    {isSearching 
                      ? 'No questions match your search. Try different keywords.'
                      : activeFilter === 'my_questions'
                        ? "You haven't asked any questions yet."
                        : activeFilter === 'my_answers'
                          ? "You haven't answered any questions yet."
                          : 'Be the first to ask a question!'}
                  </p>
                  {!isSearching && !activeFilter && (
                    <Button onClick={() => navigate('/forum/new')} className="bg-[#0A7EA4]">
                      <Plus className="w-4 h-4 mr-2" />
                      Ask Question
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {questions.map((question) => {
                  const statusConfig = STATUS_CONFIG[question.status];
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <Card 
                      key={question.id}
                      className="hover:shadow-md transition-shadow cursor-pointer group"
                      onClick={() => navigate(`/forum/question/${question.id}`)}
                      data-testid={`question-card-${question.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex gap-3">
                          {/* User Avatar */}
                          <UserInitials name={question.user_name} role={question.user_role} />
                          
                          <div className="flex-1 min-w-0">
                            {/* Badges Row */}
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                              {question.is_hot && (
                                <Badge className="bg-orange-100 text-orange-700 text-xs">
                                  <Flame className="w-3 h-3 mr-1" />
                                  Hot
                                </Badge>
                              )}
                              {question.needs_help && (
                                <Badge className="bg-red-100 text-red-700 text-xs">
                                  <HelpCircle className="w-3 h-3 mr-1" />
                                  Needs Help
                                </Badge>
                              )}
                              <Badge className={`${statusConfig.color} text-xs`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                              {question.topic_name && (
                                <Badge variant="outline" className="text-xs bg-gray-50 max-w-full whitespace-normal break-words text-left">
                                  {question.topic_name}
                                </Badge>
                              )}
                              {question.mmd_location && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                                  {question.mmd_location.replace(' MMD', '')}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Title */}
                            <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-[#0A7EA4]">
                              {question.title}
                            </h3>
                            
                            {/* Content Preview */}
                            <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                              {question.content}
                            </p>
                            
                            {/* Meta Row */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span>{question.user_name}</span>
                                {question.user_role !== 'user' && (
                                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                                    {question.user_role === 'admin' ? 'Admin' : 'Mod'}
                                  </Badge>
                                )}
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDistanceToNow(new Date(question.created_at), { addSuffix: true })}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="w-3 h-3" />
                                  {question.answer_count}
                                </span>
                              </div>
                              
                              {/* Copy Link */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="opacity-0 group-hover:opacity-100 transition-opacity h-7 px-2"
                                onClick={(e) => { e.stopPropagation(); copyLink(question.id); }}
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                <span className="text-xs">Copy Link</span>
                              </Button>
                            </div>
                          </div>
                          
                          <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 self-center" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="mt-6">
                <Pagination
                  currentPage={page}
                  totalItems={total}
                  itemsPerPage={itemsPerPage}
                  onPageChange={(newPage) => setPage(newPage)}
                  onItemsPerPageChange={(newSize) => {
                    setItemsPerPage(newSize);
                    setPage(1);
                  }}
                />
              </div>
            )}
          </div>
        </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default ForumPage;
